import React, {useEffect, useState} from 'react';
import {Box} from '@mui/material';
import colors from 'shared/aptitude/colors';
import Button from 'src/components/button';
import FileSelector from 'shared/account-settings/components/FileSelector/FileSelector';
import {Field, Form, Formik} from 'formik';
import {identity, omit} from 'lodash';
import UploadTypeService, {UploadDetails} from 'src/services/UploadTypeService';
import {acceptedFileTypes, fileSizeInMegabytes} from './utils';

interface UploadedFile {
  publicUrl: string;
}

type ValuesObject = Record<string, string>;

interface FileUploadFormProps {
  uploadFile: (file: File, string: string, options?: ValuesObject) => Promise<{publicUrl: string}>;
  flashMessage: (string: string) => void;
  title?: string;
  fileDescription: string;
  children: React.ReactNode;
  UploadedFileElement?: ({uploadedFile}: {uploadedFile: UploadedFile}) => React.ReactElement;
  Disclaimer?: () => React.ReactElement;
  validateOptions?: (values: ValuesObject) => ValuesObject;
  modifyOptions?: (values: ValuesObject) => ValuesObject;
  initialOptionsValues?: ValuesObject;
  uploadType: string;
  uploadDetails?: UploadDetails;
  resetAfterSubmit?: boolean;
}

const FileUploadForm = (props: FileUploadFormProps) => {
  const [uploadedFile, setUploadedFile] = useState<UploadedFile | null>(null);
  const [uploadDetails, setUploadDetails] = useState<UploadDetails>(
    props.uploadDetails || {
      maxFileSize: 1024 * 1024 * 20,
      allowedFileTypes: ['all'],
    }
  );

  useEffect(() => {
    if (!props.uploadDetails) {
      UploadTypeService.show(props.uploadType).then(setUploadDetails);
    }
  }, [props.uploadType]);

  const onSubmit = (values, {resetForm, setFieldValue}): Promise<void> => {
    let options = omit(values, ['file']);

    if (props.modifyOptions) {
      options = props.modifyOptions(options);
    }

    return props.uploadFile(values.file, props.uploadType, options).then((response): void => {
      props.flashMessage(`${!!values.file && values.file.name} successfully uploaded!`);
      setUploadedFile({publicUrl: response.publicUrl});
      setFieldValue('file', null);
      props.resetAfterSubmit && resetForm();
    });
  };

  const validate = (values: ValuesObject): ValuesObject => {
    let errors: Record<string, string> = {};

    if (!values.file) {
      errors.file = 'Must select file';
    }

    if (props.validateOptions) {
      errors = {...errors, ...props.validateOptions(values)};
    }

    return errors;
  };

  return (
    <Formik
      initialValues={props.initialOptionsValues ? props.initialOptionsValues : {}}
      onSubmit={onSubmit}
      validate={validate}
    >
      {({isValid, isSubmitting, dirty}): React.ReactElement => (
        <Form data-testid="file-selector-form">
          <Box
            py={6}
            px={7}
            bgcolor={colors.white}
            border={1}
            borderColor={colors.n200}
            borderRadius="4px"
            className="account-settings-form shared-font body"
          >
            {props.title && (
              <Box pb={2}>
                <div className="shared-font title bold">{props.title}</div>
              </Box>
            )}
            {props.children}
            <Box py={3}>
              <Box mb={1}>
                <div className="shared-font subheading bold">{props.fileDescription}</div>
              </Box>
              Max file size: {fileSizeInMegabytes(uploadDetails.maxFileSize)}MB
            </Box>
            <Box>
              <Field name="file" type="file">
                {({field: {name, value}, form: {setFieldValue}}): React.ReactElement => (
                  <FileSelector
                    getSelectedFile={(file): void => setFieldValue(name, file)}
                    uploading={isSubmitting}
                    fileName={value ? value.name : ''}
                    acceptedFileTypes={acceptedFileTypes(uploadDetails.allowedFileTypes)}
                  />
                )}
              </Field>
            </Box>
            {props.Disclaimer && <props.Disclaimer />}
            <Box mt={6} display="flex" justifyContent="flex-end">
              <Button
                color="primary"
                type="submit"
                disabled={!dirty || !isValid || isSubmitting}
                margin={false}
                buttonName="form-footer save"
                size="medium"
                testId="confirm-upload-button"
              >
                Confirm Upload
              </Button>
            </Box>
            {uploadedFile && props.UploadedFileElement && (
              <props.UploadedFileElement uploadedFile={uploadedFile} />
            )}
          </Box>
        </Form>
      )}
    </Formik>
  );
};

FileUploadForm.defaultProps = {
  resetAfterSubmit: true,
  modifyOptions: identity,
};

export default FileUploadForm;
